import { useTranslation } from 'react-i18next'
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'

import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import {
  submitTestAvitoLead,
  type TestAvitoLeadResult,
} from '@/api/inspection-leads'
import { formatPhoneInput } from '@/utils/phone'

interface Props {
  onClose: () => void
}

interface FormState {
  name: string
  phone: string
  region: string
  clientId: string
  itemId: string
  itemLink: string
  withoutItem: boolean
}

const DEFAULTS: FormState = {
  name: 'Тест Клиент',
  phone: '+7 999 000 0000',
  region: 'Москва',
  clientId: 'AvitoTest',
  itemId: '',
  itemLink: '',
  withoutItem: false,
}

/**
 * Модалка «Сгенерировать тестовый лид». Отправляет payload в edge-функцию
 * `avito-lead-test` (прокси), та подставляет X-Webhook-Token и зовёт
 * /avito-lead — тестовый лид проходит весь боевой путь.
 *
 * Чекбокс «без itemId/itemLink» — для отладки ветки заявки «с баннера».
 */
export function TestAvitoLeadDialog({ onClose }: Props) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  const [form, setForm] = useState<FormState>(DEFAULTS)
  const [result, setResult] = useState<TestAvitoLeadResult | null>(null)

  const mut = useMutation({
    mutationFn: async (): Promise<TestAvitoLeadResult> => {
      const name = form.name.trim()
      const phone = form.phone.trim()
      const region = form.region.trim()
      const clientId = form.clientId.trim()
      if (!name || !phone || !region || !clientId) {
        return {
          status: 'error',
          reason: 'missing_fields',
          missing: { name: !name, phone: !phone, region: !region, clientId: !clientId },
        }
      }

      let itemId: number | null = null
      if (!form.withoutItem) {
        const raw = form.itemId.trim()
        if (raw) {
          if (!/^\d+$/.test(raw)) {
            return { status: 'error', reason: 'bad_item_id', detail: t('leads.test.itemIdMustBeNumber') }
          }
          itemId = Number(raw)
        }
      }
      const itemLink = form.withoutItem ? null : (form.itemLink.trim() || null)

      return submitTestAvitoLead({ name, phone, region, clientId, itemId, itemLink })
    },
    onSuccess: (r) => {
      setResult(r)
      if (r.status === 'ok') {
        void qc.invalidateQueries({ queryKey: ['inspection-leads'] })
      }
    },
  })

  const pending = mut.isPending
  const isOk = result?.status === 'ok'

  return (
    <Modal
      title={t('leads.test.title')}
      onClose={onClose}
      footer={
        isOk ? (
          <Button variant="primary" onClick={onClose}>
            {t('common.done')}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={pending}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setResult(null)
                mut.mutate()
              }}
              disabled={pending}
            >
              {pending ? t('leads.test.sending') : t('leads.test.create')}
            </Button>
          </>
        )
      }
    >
      <div className="flex flex-col gap-3">
        <p className="text-[length:var(--stryzh-font-size-xs)] text-[color:var(--stryzh-color-text-secondary)]">
          {t('leads.test.desc')}
        </p>

        <Input
          label={t('leads.test.clientName')}
          required
          value={form.name}
          onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
          disabled={pending || isOk}
        />
        <Input
          label={t('leads.test.phone')}
          required
          value={form.phone}
          onChange={(e) => setForm((s) => ({ ...s, phone: formatPhoneInput(s.phone, e.target.value) }))}
          disabled={pending || isOk}
          inputMode="tel"
          placeholder="+7 999 123 45 67"
        />
        <Input
          label={t('leads.test.region')}
          required
          value={form.region}
          onChange={(e) => setForm((s) => ({ ...s, region: e.target.value }))}
          disabled={pending || isOk}
        />
        <Input
          label="clientId"
          required
          value={form.clientId}
          onChange={(e) => setForm((s) => ({ ...s, clientId: e.target.value }))}
          disabled={pending || isOk}
          hint={t('leads.test.clientIdHint')}
        />

        <label className="mt-1 inline-flex items-center gap-2 text-[length:var(--stryzh-font-size-sm)] text-[color:var(--stryzh-color-text-secondary)]">
          <input
            type="checkbox"
            checked={form.withoutItem}
            onChange={(e) => setForm((s) => ({ ...s, withoutItem: e.target.checked }))}
            disabled={pending || isOk}
          />
          {t('leads.test.withoutItem')}
        </label>

        {form.withoutItem ? null : (
          <>
            <Input
              label="itemId"
              value={form.itemId}
              onChange={(e) => setForm((s) => ({ ...s, itemId: e.target.value }))}
              disabled={pending || isOk}
              hint={t('leads.test.itemIdHint')}
              placeholder="1234567890"
              inputMode="numeric"
            />
            <Input
              label="itemLink"
              value={form.itemLink}
              onChange={(e) => setForm((s) => ({ ...s, itemLink: e.target.value }))}
              disabled={pending || isOk}
              placeholder="https://www.avito.ru/..."
            />
          </>
        )}

        {result ? <ResultBlock result={result} onClose={onClose} /> : null}
      </div>
    </Modal>
  )
}

function ResultBlock({
  result,
  onClose,
}: {
  result: TestAvitoLeadResult
  onClose: () => void
}) {
  const { t } = useTranslation()
  if (result.status === 'ok') {
    return (
      <div className="rounded border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-200">
        <div className="font-medium">
          {result.duplicate ? t('leads.manual.duplicate') : t('leads.manual.created')}
        </div>
        <div className="mt-1 break-all">
          ID: <code className="font-mono">{result.leadId}</code>
        </div>
        <Link
          to={`/inspection-leads/${result.leadId}`}
          onClick={onClose}
          className="mt-2 inline-block underline"
        >
          {t('leads.manual.openCard')}
        </Link>
      </div>
    )
  }
  // session_expired (нет токена на фронте) и invalid_session (сервер отклонил
  // токен) — обе про протухшую сессию: показываем человеку понятный текст.
  const sessionLost =
    result.reason === 'session_expired' || result.reason === 'invalid_session'
  if (sessionLost) {
    return (
      <div className="rounded border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
        <div className="font-medium">{t('leads.test.sessionExpired')}</div>
        <div className="mt-1">{t('leads.test.sessionExpiredBody')}</div>
      </div>
    )
  }
  const missingLabel = result.missing
    ? Object.entries(result.missing)
        .filter(([, v]) => v)
        .map(([k]) => k)
        .join(', ')
    : ''
  return (
    <div className="rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200">
      <div className="font-medium">{t('leads.test.error', { reason: result.reason })}</div>
      {missingLabel ? <div className="mt-1">{t('leads.test.missing', { fields: missingLabel })}</div> : null}
      {result.detail ? <div className="mt-1 break-all">{result.detail}</div> : null}
    </div>
  )
}
